import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Label,
  SingleSelect,
  SpinnerLoader,
  Badge,
} from 'lift-ui';
import { User, Product } from '../../types';
import * as api from '../../api';
import { transformToLegacyUser } from '../../types';
import '../../iam-support.styles.scss';

const DEFAULT_LEVEL = 'Viewer';

interface AssignPermissionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: User[];
  products: Product[];
  permissionLevels: string[];
  selectedUser?: User | null;
  onAssignAll: (userId: number, permissions: Record<string, string>) => void;
  isSubmitting?: boolean;
}

const AssignPermissionSheet: React.FC<AssignPermissionSheetProps> = ({
  open,
  onOpenChange,
  users: initialUsers,
  products,
  permissionLevels,
  selectedUser: initialSelectedUser,
  onAssignAll,
  isSubmitting = false,
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(initialSelectedUser || null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Map of product_key -> permission level (Viewer when none)
  const [permissionMap, setPermissionMap] = useState<Record<string, string>>({});
  const [originalPermissionMap, setOriginalPermissionMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      fetchAllUsers();
      if (!initialSelectedUser) {
        setSelectedUser(null);
      }
    } else {
      setSelectedUser(null);
      setPermissionMap({});
      setOriginalPermissionMap({});
    }
  }, [open, initialSelectedUser]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const usersData = await api.getAllUsersWithPermissions();
      const legacyUsers = usersData.map(transformToLegacyUser);
      setAllUsers(legacyUsers);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
      setAllUsers(initialUsers || []);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (initialSelectedUser) {
      if (allUsers.length > 0) {
        const freshUser = allUsers.find(u => u.id === initialSelectedUser.id);
        setSelectedUser(freshUser || initialSelectedUser);
      } else {
        setSelectedUser(initialSelectedUser);
      }
    } else {
      setSelectedUser(null);
    }
  }, [initialSelectedUser, allUsers]);

  // Build permission map when user changes (show Viewer when nothing is set)
  useEffect(() => {
    if (!selectedUser) {
      setPermissionMap({});
      setOriginalPermissionMap({});
      return;
    }
    const map: Record<string, string> = {};
    products.forEach((product) => {
      const raw =
        selectedUser.permissions?.[product.product_key] ??
        selectedUser.directPermissions?.[product.product_key];
      const level =
        raw && permissionLevels.includes(raw) ? raw : DEFAULT_LEVEL;
      map[product.product_key] = level;
    });
    setPermissionMap({ ...map });
    setOriginalPermissionMap({ ...map });
  }, [selectedUser, products, permissionLevels]);

  const permissionOptions = useMemo(
    () => permissionLevels.map((level) => ({ label: level, value: level })),
    [permissionLevels]
  );

  const handlePermissionChange = useCallback((productKey: string, value: string) => {
    setPermissionMap((prev) => ({ ...prev, [productKey]: value }));
  }, []);

  const changedCount = useMemo(() => {
    return products.filter(
      (p) => permissionMap[p.product_key] !== originalPermissionMap[p.product_key]
    ).length;
  }, [permissionMap, originalPermissionMap, products]);

  const handleSaveAll = () => {
    if (!selectedUser || changedCount === 0) return;
    onAssignAll(selectedUser.id, { ...permissionMap });
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedUser(null);
    setPermissionMap({});
    setOriginalPermissionMap({});
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="iam-support-sheet flex flex-col h-full w-[550px] sm:max-w-[550px]"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>Assign Product Permissions</SheetTitle>
          <SheetDescription className="pb-0">
            Select a user and manage all product permissions at once.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto space-y-5 py-4 px-1">
          {/* User selector */}
          <div className="space-y-2">
            <Label>User</Label>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-4">
                <SpinnerLoader className="h-5 w-5" />
                <span className="ml-2 text-sm text-gray-500">Loading users...</span>
              </div>
            ) : (
              <SingleSelect
                options={allUsers.map((user) => ({
                  label: `${user.name} (${user.email})`,
                  value: String(user.id),
                }))}
                defaultValue={selectedUser ? String(selectedUser.id) : undefined}
                onChange={(value) => {
                  if (!value) {
                    setSelectedUser(null);
                    return;
                  }
                  const userId = parseInt(value, 10);
                  const user = allUsers.find((u) => u.id === userId);
                  if (user) setSelectedUser(user);
                }}
                placeholder="Select a user"
                containerClassName="w-full"
                disabled={!!initialSelectedUser}
              />
            )}
          </div>

          {/* Product permissions grid */}
          {selectedUser && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Product Permissions</Label>
                <div className="flex items-center gap-2">
                  {selectedUser.isSuperadmin && (
                    <Badge variant="default" className="iam-support-superadmin-badge text-xs">
                      Superadmin
                    </Badge>
                  )}
                  {!selectedUser.isSuperadmin && changedCount > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {changedCount} changed
                    </Badge>
                  )}
                </div>
              </div>
              {selectedUser.isSuperadmin ? (
                <p className="text-xs text-muted-foreground -mt-1">
                  This user is a Superadmin and has full access to all products. Permissions cannot be edited individually.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground -mt-1">
                  Viewer: Read-only &bull; Editor: Read + Write &bull; Manager: Full access
                </p>
              )}
              <div className="space-y-3">
                {products.map((product) => {
                  const currentValue = permissionMap[product.product_key] ?? DEFAULT_LEVEL;
                  const originalValue = originalPermissionMap[product.product_key] ?? DEFAULT_LEVEL;
                  const isChanged = currentValue !== originalValue;
                  return (
                    <div
                      key={product.product_key}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectedUser.isSuperadmin
                          ? 'border-primary/20 bg-primary/5 cursor-default'
                          : isChanged
                            ? 'border-muted-foreground/40 bg-muted-foreground/5'
                            : 'border-border bg-background'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.product_name}</p>
                        {isChanged && (
                          <p className="text-xs mt-0.5">Modified</p>
                        )}
                      </div>
                      <div className="w-[160px] flex-shrink-0">
                        {selectedUser.isSuperadmin ? (
                          <Badge variant="default" className="iam-support-superadmin-badge text-xs w-full justify-center py-1.5">
                            Superadmin
                          </Badge>
                        ) : (
                          <SingleSelect
                            options={permissionOptions}
                            defaultValue={currentValue}
                            onChange={(val) =>
                              handlePermissionChange(product.product_key, val || DEFAULT_LEVEL)
                            }
                            placeholder="Select level"
                            containerClassName="w-full"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state when no user selected */}
          {!selectedUser && !loadingUsers && (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              Select a user above to manage their permissions
            </div>
          )}
        </div>
        <SheetFooter className="border-t pt-4 mt-2">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSaveAll}
            disabled={!selectedUser || selectedUser?.isSuperadmin || changedCount === 0 || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : `Save ${changedCount > 0 ? `(${changedCount})` : ''}`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AssignPermissionSheet;
